import React, { useEffect, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

interface CoverImageProps {
  src?: string;
  title: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  // Thumbnails are too small for the placeholder title
  showTitle?: boolean;
  children?: React.ReactNode;
}

// Deep, muted grounds so white overlay buttons and chips stay readable.
const PLACEHOLDER_COLOURS = [
  '#1c2333', '#3a2e14', '#2b1f3d', '#5a1a1a', '#2f3d1c', '#6b3410',
  '#173d5c', '#4a4210', '#3b1730', '#151b3a', '#1f3d3a', '#402030',
];

// Open Library answers a missing cover with a blank 1×1 image unless asked
// to 404, which would leave an empty cover instead of the placeholder.
const coverSrc = (src: string) =>
  /^https?:\/\/covers\.openlibrary\.org\//.test(src) && !src.includes('default=')
    ? `${src}${src.includes('?') ? '&' : '?'}default=false`
    : src;

// Same title, same colour, on every device.
const colourFor = (title: string) => {
  let hash = 0;
  for (let i = 0; i < title.length; i += 1) {
    hash = (hash * 31 + title.charCodeAt(i)) | 0;
  }
  return PLACEHOLDER_COLOURS[Math.abs(hash) % PLACEHOLDER_COLOURS.length];
};

// A 2:3 cover. With no URL, or one that fails to load, it shows the title on
// a coloured block. Overlays (chips, buttons, progress) go in as children.
const CoverImage = ({ src, title, onClick, sx, showTitle = true, children }: CoverImageProps) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  const showImage = Boolean(src) && !failed;

  return (
    <Box
      onClick={onClick}
      sx={[
        {
          position: 'relative',
          aspectRatio: '2 / 3',
          overflow: 'hidden',
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: showImage ? 'action.hover' : colourFor(title),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showImage ? (
        <Box
          component="img"
          src={coverSrc(src as string)}
          alt={title}
          loading="lazy"
          onError={() => setFailed(true)}
          // A tiny "no cover" image counts as missing too
          onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
            if (e.currentTarget.naturalWidth <= 1) setFailed(true);
          }}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box
          role="img"
          aria-label={title}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center',
            px: 1.5,
            color: 'rgba(255,255,255,0.88)',
            fontWeight: 800,
            fontSize: 'clamp(11px, 1.6vw, 15px)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}
        >
          {showTitle && title}
        </Box>
      )}
      {children}
    </Box>
  );
};

export default CoverImage;
